import Breadcrumb from "@/components/base/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";
import FeedbackForm from "@/pages/about/components/FeedbackForm";

const contentScope = [
  {
    icon: "ri-book-open-line",
    title: "全集剧情介绍",
    desc: "按集梳理《年轻的母亲3》的完整剧情脉络，从归乡到和解，记录每一个关键转折点。",
  },
  {
    icon: "ri-user-heart-line",
    title: "角色深度分析",
    desc: "解读主要角色的性格、动机与成长弧线，帮助观众理解每一个选择背后的重量。",
  },
  {
    icon: "ri-newspaper-line",
    title: "最新资讯动态",
    desc: "汇总官方消息、幕后花絮、主创访谈与观众讨论，持续更新剧集相关资讯。",
  },
];

const contacts = [
  { icon: "ri-mail-line", label: "联系邮箱", value: "contact@youngmother3.example.com", href: "mailto:contact@youngmother3.example.com" },
  { icon: "ri-weibo-line", label: "官方微博", value: "@年轻的母亲3资料站", href: "#" },
  { icon: "ri-bilibili-line", label: "内容合作", value: "哔哩哔哩 · 剧集资料君", href: "#" },
];

export default function About() {
  usePageMeta(
    "关于与联系 - 年轻的母亲3",
    "了解《年轻的母亲3》资料站的定位、内容范围与联系方式，并可通过反馈表单提交你的建议。",
  );

  return (
    <div className="w-full px-4 md:px-8 py-8 md:py-10">
      <div className="max-w-[1100px] mx-auto">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于与联系" }]} />

        <header className="mt-5 mb-10 md:mb-14 max-w-3xl">
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-accent-700 uppercase tracking-wider mb-3">
            <span className="w-1.5 h-1.5 rounded-full bg-accent-500"></span>
            About us
          </span>
          <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-950 leading-tight">
            关于《年轻的母亲3》资料站
          </h1>
          <p className="mt-4 text-sm md:text-[15px] text-foreground-600 leading-relaxed">
            我们是一个专注于电视剧《年轻的母亲3》的内容整理平台，致力于为影视剧爱好者提供系统、准确、
            易读的剧集资料。无论你是刚刚入坑的新观众，还是一路追更的老粉丝，都能在这里找到想要的答案。
          </p>
        </header>

        <section className="mb-12 md:mb-16">
          <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-5">
            我们提供什么
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {contentScope.map((item) => (
              <div key={item.title} className="bg-background-50 border border-background-200 rounded-lg p-6">
                <span className="w-12 h-12 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center mb-4">
                  <i className={`${item.icon} text-2xl`}></i>
                </span>
                <h3 className="font-heading font-bold text-base text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-12 md:mb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-5">
                网站简介
              </h2>
              <div className="space-y-4 text-[15px] text-foreground-700 leading-loose">
                <p>
                  《年轻的母亲3》资料站由一群热爱家庭题材剧集的内容爱好者共同维护。我们相信，好的剧集值得被认真拆解，
                  而完整的剧情梳理与角色分析，能让观众在观剧之后获得更深的理解与共鸣。
                </p>
                <p>
                  站内的全部剧情章节、角色档案与资讯均为整理与演示内容，用于展示剧集资料的组织方式。
                  我们会持续更新内容结构，让信息更加清晰易读。
                </p>
                <p>
                  如果你对内容有任何建议，或是希望补充某个角色的分析，欢迎通过下方的反馈表单与我们联系。
                </p>
              </div>
            </div>

            <div>
              <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-5">
                联系方式
              </h2>
              <ul className="space-y-4">
                {contacts.map((c) => (
                  <li key={c.label}>
                    <a
                      href={c.href}
                      className="group flex items-center gap-4 p-4 bg-background-50 border border-background-200 rounded-lg hover:border-primary-300 transition-colors cursor-pointer"
                    >
                      <span className="w-11 h-11 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center shrink-0">
                        <i className={`${c.icon} text-xl`}></i>
                      </span>
                      <div className="min-w-0">
                        <p className="text-xs text-foreground-500">{c.label}</p>
                        <p className="text-sm font-medium text-foreground-900 group-hover:text-primary-700 transition-colors truncate">
                          {c.value}
                        </p>
                      </div>
                      <span className="ml-auto w-5 h-5 flex items-center justify-center text-foreground-300 group-hover:text-primary-600 transition-colors">
                        <i className="ri-arrow-right-up-line text-lg"></i>
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="mb-6">
          <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-2">
            反馈与建议
          </h2>
          <p className="text-sm text-foreground-600 mb-6 leading-relaxed">
            填写以下表单，把你的想法告诉我们。我们会认真阅读每一条反馈，并持续优化站内内容。
          </p>
          <FeedbackForm />
        </section>
      </div>
    </div>
  );
}